import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import { studentApi } from '@/features/student/api';
type Doc = {
  id: string;
  title: string;
  category: string;
  download_url: string;
  publication_status: string;
};
export function CourseDocuments({ code }: { code: string }) {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('Материалы');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    setDocs([]);
    setError('');
    setFile(null);
    setTitle('');
    if (code)
      void apiRequest<Doc[]>(`/teacher/documents?discipline_code=${encodeURIComponent(code)}`, {
        channel: 'platform',
      })
        .then((rows) => {
          if (active) setDocs(rows);
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    return () => {
      active = false;
    };
  }, [code]);
  const run = async (action: () => Promise<unknown>) => {
    setSaving(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="space-y-3 text-sm">
      <h3>Документы и образцы дисциплины</h3>
      {error && <p role="alert">{error}</p>}
      <input
        className="bg-slate-900 text-white border border-slate-700"
        aria-label="Название документа"
        placeholder="Название"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <select
        className="bg-slate-900 text-white border border-slate-700"
        aria-label="Категория документа"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        <option>Материалы</option>
        <option>Образцы</option>
        <option>Силлабус</option>
        <option>Литература</option>
      </select>
      <input
        className="bg-slate-900 text-white border border-slate-700"
        type="file"
        aria-label="Файл документа"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
      <button
        disabled={!code || !title.trim() || !file || saving}
        onClick={() =>
          void run(async () => {
            const attachment = await studentApi.uploadCourseFile(code, file!);
            await apiRequest('/teacher/documents', {
              method: 'POST',
              channel: 'platform',
              body: { discipline_code: code, title, category, attachment_id: attachment.id },
            });
            setDocs(
              await apiRequest<Doc[]>(
                `/teacher/documents?discipline_code=${encodeURIComponent(code)}`,
                { channel: 'platform' },
              ),
            );
            setTitle('');
            setFile(null);
          })
        }
      >
        Сохранить документ
      </button>
      {docs.map((doc) => (
        <article className="border border-slate-700 p-3" key={doc.id}>
          <p>
            {doc.title} · {doc.category} · {doc.publication_status}
          </p>
          <button
            disabled={saving}
            onClick={() => void run(() => studentApi.downloadCourseFile(doc.download_url))}
          >
            Скачать
          </button>
          <button
            disabled={saving}
            onClick={() =>
              void run(async () => {
                const published = doc.publication_status !== 'Опубликовано';
                await apiRequest(
                  `/teacher/documents/${doc.id}/publication?published=${published}`,
                  { method: 'PUT', channel: 'platform' },
                );
                setDocs((prev) =>
                  prev.map((d) =>
                    d.id === doc.id
                      ? { ...d, publication_status: published ? 'Опубликовано' : 'Черновик' }
                      : d,
                  ),
                );
              })
            }
          >
            {doc.publication_status === 'Опубликовано' ? 'Снять с публикации' : 'Опубликовать'}
          </button>
        </article>
      ))}
      {!docs.length && <p>Документы еще не добавлены.</p>}
    </section>
  );
}
